import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import logo from './logo.svg';
import AdminGuard from './components/AdminGuard';
import UserMenu from './components/UserMenu';
import Home from './pages/Home';
import DiretoriaDocs from './pages/DiretoriaDocs';
import AdminAccess from './pages/AdminAccess';
import UploadDocs from './pages/UploadDocs';
import UploaderGuard from './components/UploaderGuard';
import { useUserAccess } from './hooks/useUserAccess';
import { useDreAccess } from './hooks/useDreAccess';
import RegionalGuard from './components/RegionalGuard';
import RegionalDocs from './pages/RegionalDocs';
import AdmcenGuard from './components/AdmcenGuard';
import AdmcenDocs from './pages/AdmcenDocs';
import DreGuard from './components/DreGuard';
import Dre from './pages/Dre';
import DreConsolidadoGuard from './components/DreConsolidadoGuard';
import DreDiretoria from './pages/DreDiretoria';

const providers = [
  { key: 'aad', label: 'Login SSO Cvale', path: '/.auth/login/aad' }
];

// Símbolo da Microsoft (4 quadrados nas cores da marca) — usado no botão de SSO.
function MicrosoftLogo() {
  return (
    <svg className="ms-logo" width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadUser() {
      try {
        const response = await fetch('/.auth/me', {
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) { setUser(null); setLoading(false); return; }

        const data = await response.json();
        const principal = data.clientPrincipal || data;
        if (principal && principal.userDetails) setUser(principal);
        else setUser(null);
      } catch (e) {
        setError('Falha de autenticação.');
        setUser(null);
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, []);

  const handleLogin = (path) => {
    const redirectUri = encodeURIComponent(window.location.pathname);
    window.location.href = `${path}?post_login_redirect_uri=${redirectUri}`;
  };

  const handleLogout = () => {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  };

  if (loading) {
    return (
      <div className="app-shell">
        <div className="status-card">
          <span className="status-title">Carregando aplicação...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-shell">
        <span className="login-glow" aria-hidden="true" />
        <div className="auth-card">
          <img src={logo} alt="C.Vale" className="login-logo" />
          <h1>Cvale Portal De Dados</h1>
          <p className="login-subtitle">Acesse com sua conta corporativa</p>
          {error && <p className="error-message">{error}</p>}
          <div className="button-grid">
            {providers.map((provider) => (
              <button key={provider.key} className="auth-button"
                onClick={() => handleLogin(provider.path)}>
                <MicrosoftLogo />
                {provider.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AuthenticatedApp user={user} onLogout={handleLogout} />
    </BrowserRouter>
  );
}

function AuthenticatedApp({ user, onLogout }) {
  const { isAdmin, canUpload, canReadDreGlobal, canAccessRegional, canAccessAdmcen, displayName } = useUserAccess();
  const { isGerente: isDreGerente } = useDreAccess();

  // A planilha consolidada do DRE precisa de quase toda a largura da tela.
  const location = useLocation();
  const mainWide = location.pathname === '/dre-consolidado';

return (
    <div className="app-shell-authenticated">
      <nav className="navbar">
        <span className="navbar-glow" aria-hidden="true" />
        <div className="navbar-brand">
          <span className="brand-logo">C<span className="dot">.</span>Vale</span>
          <span className="brand-sep" />
          <span className="brand-title">Portal de Dados</span>
        </div>
        <div className="navbar-links">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/diretoria">Diretoria</NavLink>
          {canAccessRegional && <NavLink to="/regional">Regional</NavLink>}
          {canAccessAdmcen && <NavLink to="/admcen">ADMCEN</NavLink>}
          {isDreGerente && <NavLink to="/dre">DRE</NavLink>}
          {canReadDreGlobal && <NavLink to="/dre-consolidado">DRE Consolidado</NavLink>}
          {canUpload && <NavLink to="/upload">Enviar Documentos</NavLink>}
          {isAdmin && <NavLink to="/admin">Admin</NavLink>}
        </div>
        <div className="navbar-user">
          <UserMenu displayName={displayName} email={user.userDetails} onLogout={onLogout} />
        </div>
      </nav>

      <main className={`main-content${mainWide ? ' main-content--full' : ''}`}>
        <Routes>
          <Route path="/" element={<Home user={user} />} />
          <Route path="/diretoria" element={<DiretoriaDocs user={user} />} />
          <Route path="/regional" element={
            <RegionalGuard>
              <RegionalDocs user={user} />
            </RegionalGuard>
          } />
          <Route path="/admcen" element={
            <AdmcenGuard>
              <AdmcenDocs user={user} />
            </AdmcenGuard>
          } />
          <Route path="/dre" element={
            <DreGuard>
              <Dre user={user} />
            </DreGuard>
          } />
          <Route path="/dre-consolidado" element={
            <DreConsolidadoGuard>
              <DreDiretoria user={user} />
            </DreConsolidadoGuard>
          } />
          <Route path="/upload" element={
            <UploaderGuard>
              <UploadDocs user={user} />
            </UploaderGuard>
          } />
          <Route path="/admin" element={
            <AdminGuard>
              <AdminAccess user={user} />
            </AdminGuard>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;